import React from "react";
import ReactApexChart from "react-apexcharts";
import { tokens } from "../../../../theme";
import { useTheme } from "@mui/material/styles";

const modeConfig = {
  1: { color: "#40a57b", label: "Mode 1 : CHP max power + aFRR down" },
  2: { color: "#a0befb", label: "Mode 2 : CHP e-load following" },
  3: { color: "#625a76", label: "Mode 3 : CHP technical minimum" },
  4: { color: "#f4c276", label: "Mode 4 : CHP off" },
};

export default function FilledOperationalModeFinalChart({chpData}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  console.log("chpData final filled", chpData);
  
  // Add null check and fallback
  if (!chpData || (!chpData['CHP'] && !Array.isArray(chpData))) {
    return (
      <div style={{
        pl: 2,
        pr: 2,
        textAlign: "center",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
        borderRadius: 8,
        backgroundColor: "#fff",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        pb: 4,
        height: 350,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Process the data to create time ranges
  // Handle both nested and flat data structures
  let chp01Data;
  if (chpData['CHP'] && chpData['CHP']['CHP-01']) {
    chp01Data = chpData['CHP']['CHP-01'];
  } else if (Array.isArray(chpData)) {
    chp01Data = chpData;
  } else {
    chp01Data = [];
  }
  
  // Find the latest data just before current hour
  const now = new Date();
  const latestData = chp01Data
    .filter(entry => new Date(entry.datetime) <= now)
    .sort((a, b) => new Date(b.datetime) - new Date(a.datetime))[0];
  
  
  // Convert point data to time ranges - create individual ranges for each data point
  const timeRanges = [];
  for (let i = 0; i < chp01Data.length; i++) {
    const currentEntry = chp01Data[i];
    const currentMode = Math.round(currentEntry.value);
    const currentTime = new Date(currentEntry.datetime);
    
    let endTime;
    
    // For each entry, create a time range from current time to next entry time
    if (i < chp01Data.length - 1) {
      const nextEntry = chp01Data[i + 1];
      endTime = new Date(nextEntry.datetime);
    } else {
      // For the last entry, add 1 hour to show it properly
      endTime = new Date(currentTime.getTime() + 60 * 60 * 1000);
    }
    
    timeRanges.push({
      from: currentTime.toISOString(),
      to: endTime.toISOString(),
      value: 100, // Fixed value for area chart
      mode: currentMode
    });
  }

  console.log("timeRanges", timeRanges);

  // Create series data for each mode
  const seriesData = Object.keys(modeConfig).map(mode => {
    const modeData = timeRanges.filter(entry => entry.mode.toString() === mode);
    const points = modeData.flatMap((entry, index) => {
      const points = [
        { x: new Date(entry.from).getTime(), y: entry.value },
        { x: new Date(entry.to).getTime(), y: entry.value }
      ];
      
      // Add null point if this isn't the last entry and next entry isn't consecutive
      if (index < modeData.length - 1) {
        const nextEntry = modeData[index + 1];
        if (entry.to !== nextEntry.from) {
          points.push({ x: new Date(entry.to).getTime(), y: null });
        }
      }
      
      return points;
    });

    return {
      name: modeConfig[mode].label,
      data: points,
      color: modeConfig[mode].color
    };
  });



  console.log("seriesData", seriesData);

  const options = {
    chart: {
      type: "area",
      height: 350,
      zoom: { enabled: false }
    },
    stroke: {
      curve: "stepline",
      width: 2
    },
    fill: {
      type: "solid",
      opacity: 0.5
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
        format: "dd MMM HH:mm"
      }
    },
    yaxis: {
      min: 90,
      max: 105,
      tickAmount: 10,
      labels: {
        formatter: function(value) {
          return "";
        }
      },
      title: {
        text: "Operational Mode"
      }
    },
    annotations: {
      xaxis: [
        {
          x: new Date().getTime(),
          borderColor: '#FF4560',
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            borderColor: '#FF4560',
            style: {
              color: '#fff',
              background: '#FF4560'
            },
            //show minutes 2 digits
            text: `Latest Data ${new Date(latestData.datetime).getHours()}:${new Date(latestData.datetime).getMinutes().toString().padStart(2, '0')} : Mode ${latestData.value}`
          }
        }
      ]
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      style: {
        fontSize: '14px'
      },
      custom: function({series, seriesIndex, dataPointIndex, w}) {
        const x = w.globals.seriesX[seriesIndex][dataPointIndex];
        const date = new Date(x);
        const pad = n => n < 10 ? '0' + n : n;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const formattedDate = `${pad(date.getDate())} ${months[date.getMonth()]} ${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        let modeLabel = w.config.series[seriesIndex].name;
        if (modeLabel.endsWith(':')) modeLabel = modeLabel.slice(0, -1);
        // Use the color from the current series
        const color = w.config.series[seriesIndex].color || '#40a57b';
        return `
          <div style="min-width:200px;">
            <div style="background:#f5f6fa;padding:8px 0 8px 0;border-radius:8px 8px 0 0;text-align:center">
              ${formattedDate}
            </div>
            <div style="display:flex;text-align:center;align-items:center;gap:8px;padding:12px 10px 10px 10px;">
              <span style="display:inline-block;width:12px;height:12px;background:${color};border-radius:50%;margin-right:4px;"></span>
              <span style="color:#222;">${modeLabel}</span>
            </div>
          </div>
        `;
      }
    },
    colors: Object.values(modeConfig).map(mode => mode.color),
    title: {
      text: "Final Calculation Results",
      align: "left",
      style: {
        fontSize: "16px",
        fontWeight: "bold"
      }
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      onItemClick: {
        toggleDataSeries: false
      }
    }
  };

  return (
    <div style={{
      padding: 18,
      textAlign: "center",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
      borderRadius: 8,
      backgroundColor: "#fff",
      justifyContent: "center",
      alignItems: "center",
      alignSelf: "center",
    }}>
      <ReactApexChart options={options} series={seriesData} type="area" height={350} />
    </div>
  );
}



